import { KlausTouchpoint, klausUrl } from '@app/constants/klaus';
import { useUser } from '@app/context/UserContext';
import { useQueryParams } from '@app/hooks/useQuestionaireRoute';
import { Loading } from '@components/ui';
import { useKlausHandoffMutation } from '@dieterApi/user/useKlausHandoffMutation';
import { useUserLoginMutation } from '@dieterApi/user/useUserLoginMutation';
import { useUserLogoutMutation } from '@dieterApi/user/useUserLogoutMutation';
import { Button } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Login } from '../Login/Login';
import { MFAVerify } from '../UserActions/MFA/MFAVerify';

const TOUCHPOINTS: KlausTouchpoint[] = ['banner', 'modal', 'task', 'context-panel'];

/**
 * /zu-klaus?next=/dashboard/schulung[&authtoken=…][&src=banner]
 *
 * `src` geht mit an den Server und landet als Event `klaus.handoff` in der
 * Sequence-Engine (Auswertung S16: welche Mail bzw. welcher Touchpoint wirkt).
 *
 * Springt ohne neuen Login nach Klaus (dev/klaus-sync/addendum-v1.5.md). Ziel
 * der S16-Mails (mit authtoken), des Menüeintrags "Zu Klaus" und der
 * Klaus-Touchpoints im Dashboard.
 *
 * Bewusst keine PrivateRoute: die rendert ihre Kinder schon vor dem Login per
 * authtoken, wenn noch eine alte Session besteht — der Sprung liefe dann unter
 * dem falschen User. Hier gilt die Reihenfolge explizit: erst den Token
 * einlösen, dann den Klaus-Link holen.
 */
export function KlausHandoff() {
  const { t, i18n } = useTranslation();
  const query = useQueryParams();
  const authtoken = query.get('authtoken') || '';
  const next = query.get('next');
  const src = query.get('src');

  const { user, userLoading } = useUser();
  const [login] = useUserLoginMutation();
  const [logout] = useUserLogoutMutation({ skipRedirect: true });
  const [handoff] = useKlausHandoffMutation();

  // Token zuerst einlösen; erst danach darf der Link geholt werden.
  const [tokenDone, setTokenDone] = useState(!authtoken);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const started = useRef(-1);

  useEffect(() => {
    if (!authtoken || tokenDone) return;
    const loginWithToken = () =>
      login({ variables: { email: '', password: '', token: authtoken, locale: i18n.language.slice(0, 2) } as any });
    (user ? logout().then(loginWithToken) : loginWithToken())
      .then(() => setTokenDone(true))
      .catch(() => {
        // Abgelaufener Mail-Link: normal anmelden lassen, danach geht es weiter.
        const url = new URL(window.location.href);
        url.searchParams.delete('authtoken');
        window.location.replace(url.toString());
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authtoken]);

  const ready = tokenDone && !userLoading && !!user && user.mfaVerified;

  useEffect(() => {
    if (!ready || started.current === attempt) return;
    started.current = attempt;
    handoff({ variables: { next, src } })
      .then(({ data }) => {
        if (data?.klausHandoffUrl) window.location.replace(data.klausHandoffUrl);
        else setError('KLAUS_NOT_CONFIGURED');
      })
      .catch((e) => {
        const message = String(e?.message ?? '');
        if (message.includes('KLAUS_NOT_CONFIGURED')) {
          // Ohne Kopplung bleibt nur die Klaus-Website (Cross-Promo-Touchpoints).
          window.location.replace(
            klausUrl(TOUCHPOINTS.includes(src as KlausTouchpoint) ? (src as KlausTouchpoint) : 'banner')
          );
          return;
        }
        setError(message);
      });
  }, [ready, attempt, handoff, next, src]);

  if (!tokenDone || userLoading)
    return (
      <Centered>
        <Loading />
      </Centered>
    );
  if (!user) return <Login />;
  if (!user.mfaVerified) return <MFAVerify />;
  if (error) {
    return (
      <Centered>
        <div className="max-w-md text-center flex flex-col gap-4">
          <p>
            {t('route.klaus-handoff.error', 'Der Wechsel zu Klaus hat nicht geklappt. Bitte versuche es noch einmal.')}
          </p>
          <Button
            variant="contained"
            onClick={() => {
              setError(null);
              setAttempt((a) => a + 1);
            }}
          >
            {t('route.klaus-handoff.retry', 'Erneut versuchen')}
          </Button>
        </div>
      </Centered>
    );
  }
  return (
    <Centered>
      <div className="flex flex-col items-center gap-4">
        <Loading />
        <p>{t('route.klaus-handoff.redirecting', 'Du wirst zu Klaus weitergeleitet …')}</p>
      </div>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center justify-center mt-24">{children}</div>;
}
